import logoUrl from '../assets/saragossa-logo.svg';
import type { User } from '../lib/types';
import { useTheme } from '../lib/theme';

export interface HeaderTab<K extends string> {
  key: K;
  label: string;
}

interface HeaderProps<K extends string> {
  user?: User;
  tabs?: ReadonlyArray<HeaderTab<K>>;
  activeTab?: K;
  onTabChange?: (key: K) => void;
  /** Present for leadership — opens the view-access settings panel. */
  onOpenSettings?: () => void;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/** Top bar in the Client Dashboard's style: brand, underline tabs, tools on the right. */
export function Header<K extends string>({ user, tabs, activeTab, onTabChange, onOpenSettings }: HeaderProps<K>) {
  const { theme, toggle } = useTheme();
  return (
    <header className="topbar">
      <div className="topbar__brand">
        <img src={logoUrl} alt="Saragossa" className="select-none brand-art" draggable={false} />
      </div>

      <nav className="topbar__tabs">
        {tabs?.map((t) => (
          <button
            key={t.key}
            onClick={() => onTabChange?.(t.key)}
            className={'topbar__tab' + (t.key === activeTab ? ' active' : '')}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {user && (
        <div className="topbar__right">
          <button
            onClick={toggle}
            className="topbar__icon"
            aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            title={theme === 'light' ? 'Dark mode' : 'Light mode'}
          >
            {theme === 'light' ? <MoonIcon /> : <SunIcon />}
          </button>
          {onOpenSettings && (
            <button onClick={onOpenSettings} className="topbar__icon" aria-label="Settings" title="Settings">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
              </svg>
            </button>
          )}
          <div className="topbar__user" title={`${user.territory} · ${user.currency} · ${user.role}`}>
            <span className="topbar__avatar">{initials(user.displayName)}</span>
            <span className="hidden md:inline">{user.displayName}</span>
          </div>
          <a href="/logout" className="topbar__signout">Sign out</a>
        </div>
      )}
    </header>
  );
}

function SunIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.5v2.2M12 19.3v2.2M4.6 4.6l1.6 1.6M17.8 17.8l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.6 19.4l1.6-1.6M17.8 6.2l1.6-1.6" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.5 14.2A8.5 8.5 0 1 1 9.8 3.5a6.8 6.8 0 0 0 10.7 10.7z" />
    </svg>
  );
}